import React, { useEffect, useState } from 'react'
import { Goal } from 'Generated/graphql-types'
import { NEEDS_PROGRESS_UPDATE } from 'Apollo/Queries/NeedsProgressUpdate'
import { useHistory } from 'react-router'
import { MessagePopover } from '../MessagePopover/MessagePopover'
import { ProgressUpdateDialog } from '../ProgressUpdateDialog/ProgressUpdateDialog'
import { ProgressUpdateIntroModal } from './ProgressUpdateIntroModal'
import { useProgressUpdatePrompt } from './useProgressUpdatePrompt'

type ProgressUpdatesProps = {
  open: boolean
  onClose: () => void
  goalId: number
  goal?: Goal
  taskId?: number
}

export const ProgressUpdates = ({
  open,
  onClose,
  goalId,
  goal,
  taskId,
}: ProgressUpdatesProps) => {
  const history = useHistory()
  const [step, setStep] = useState<'intro' | 'dialog' | 'success' | 'error'>(
    'intro'
  )
  const { loading, needsProgressUpdate, markPrompted } =
    useProgressUpdatePrompt()

  useEffect(() => {
    if (!loading && needsProgressUpdate) setStep('intro')
    else setStep('dialog')
  }, [loading, needsProgressUpdate])

  const handleUpdateComplete = async (result: {
    success: boolean
    error?: string
  }) => {
    try {
      if (needsProgressUpdate) await markPrompted()
      setStep(result.success ? 'success' : 'error')
    } catch {
      setStep('error')
    }
  }

  const handleNavigateToGoal = () => {
    onClose()
    history.push(`/impact/goals/${goalId}`)
  }

  const handleNavigateToUpdates = () => {
    onClose()
    history.push(`/impact/goals/${goalId}?section=updates`)
  }

  if (!open) return null

  if (step === 'intro') {
    return (
      <ProgressUpdateIntroModal
        open
        onClose={onClose}
        onGetStarted={() => {
          markPrompted({
            refetchQueries: [{ query: NEEDS_PROGRESS_UPDATE }],
            awaitRefetchQueries: true,
          })
          setStep('dialog')
        }}
      />
    )
  }

  if (step === 'dialog') {
    return (
      <ProgressUpdateDialog
        open
        goalId={goalId}
        taskId={taskId}
        onUpdateComplete={handleUpdateComplete}
        onClose={onClose}
        standalone={false}
      />
    )
  }

  if (step === 'success') {
    return (
      <MessagePopover
        open
        onClose={onClose}
        alertColor='success'
        title='Great job!'
        alertMessage='Your update has been successfully added'
        content={
          goal ? (
            <>
              Your update is stored in the Progress Update section of the Goal:
              <br />
              <br />
              <strong>{goal.title}</strong>
              <br />
              <br />
              Your team members can now see your progress, but don&apos;t
              worry—only you can edit or delete it at any time.
            </>
          ) : undefined
        }
        primaryButton={{
          text: 'View Goal',
          onClick: handleNavigateToGoal,
        }}
        showConfirmButtons={false}
      />
    )
  }

  if (step === 'error') {
    return (
      <MessagePopover
        open
        onClose={onClose}
        alertColor='error'
        title='Something happened'
        alertMessage='You cannot update this activity later'
        content="We could not add your update, but you can add it directly to your Goal by clicking the 'Add Update' button in its Progress Updates section."
        primaryButton={{
          text: 'View Update',
          onClick: handleNavigateToUpdates,
        }}
        showConfirmButtons={false}
      />
    )
  }

  return null
}
